import { useEffect, useState, type FormEvent } from 'react';
import { extractOtpDigits } from '@icebreaker/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { typography } from '@icebreaker/shared/design';
import { ERROR_CODES, type ApiErrorBody } from '@icebreaker/shared/errors';
import { STAFF_LIMITS } from '@icebreaker/shared/staff';
import { Button, Card, ErrorText, Field, Input, PageShell } from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { BrandLock } from '@/shared/ui';
import { safeReturnPath } from '../model/return-path';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import {
  selectAuth,
  selectChallenge,
  selectLoginRequestState,
  selectVerifyState,
} from '../store/auth.selectors';
import { challengeReset, staffLogin, staffVerify } from '../store/auth.slice';
import styles from './sign-in.module.css';

/**
 * OTURUM SUNUCUDA BİTTİ (B-02): kapı personeli buraya getirdiyse nedeni bir kez
 * yazılır. Metin `STAFF_SESSION_EXPIRED` kodunun çevirisidir; cümle ayrıca
 * yazılmaz. Yeni giriş denemesi bayrağı düşürür.
 */
const SESSION_EXPIRED_NOTICE: ApiErrorBody = {
  code: ERROR_CODES.STAFF_SESSION_EXPIRED,
  statusCode: 401,
  requestId: '',
};

/**
 * PANEL GİRİŞİ — e-posta + ŞİFRE + tek kullanımlık kod (panel kimliği ayrımı).
 *
 * Şifre ve kod birlikte iki faktördür; şifre adımı OTURUM AÇMAZ, yalnız kodu
 * tetikler. İki adım da `/panel/auth/*` altındadır ve ürünün OTP uçlarıyla
 * hiçbir şey PAYLAŞMAZ (ayrı tablo, ayrı imza anahtarı, ayrı audience).
 *
 * "KODLA GİR" YOLU KALDIRILDI. Eski hâlinde şifresi olmayan personel ürünün
 * parolasız OTP ucundan giriyordu; ölçülmüş P1'in zinciri tam olarak oradan
 * geçiyordu. İlk kurulum artık DAVETLE yapılır (`/kurulum`, Karar 4c) ve
 * panelde "şifremi unuttum" YOKTUR — kilitlenen personeli bir yönetici
 * yeniden davet eder.
 *
 * BURASI KAYIT YÜZEYİ DEĞİLDİR: rıza kutuları yoktur çünkü panelde hesap
 * AÇILMAZ. Hesabın var olup olmadığı bu ekranda sızdırılmaz — sunucu dört ret
 * durumunda (adres yok / şifre yanlış / askıda / şifre kurulmamış) AYNI gövdeyi
 * ve aynı süreyi döner.
 */
export function SignInScreen() {
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const auth = useAppSelector(selectAuth);
  const challenge = useAppSelector(selectChallenge);
  const requestState = useAppSelector(selectLoginRequestState);
  const verifyState = useAppSelector(selectVerifyState);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    // `navigate` bir Promise döndürür (react-router 7); gezinme beklenmez.
    // Kapı oturumsuz personeli buraya getirirken kaldığı adresi taşır (B-02).
    if (auth.isAuthenticated) void navigate(safeReturnPath(location.state), { replace: true });
  }, [auth.isAuthenticated, location.state, navigate]);

  const submitCredentials = (event: FormEvent) => {
    event.preventDefault();
    dispatch(staffLogin.request({ email: email.trim(), password }));
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    dispatch(staffVerify.request({ email: challenge.email ?? email.trim(), code }));
  };

  const changeEmail = () => {
    setPassword('');
    setCode('');
    dispatch(challengeReset());
  };

  /*
   * KOD ŞİFRENİN GİRİLDİĞİ AĞA BAĞLI (G1b). Ağ değiştiyse kodu yeniden denemek
   * işe yaramaz; tek çıkış şifre adımıdır. E-posta korunur, şifre boşalır.
   */
  const networkChanged = verifyState.error?.code === ERROR_CODES.STAFF_CODE_NETWORK_CHANGED;

  return (
    <PageShell rail="reading" as="main" className={styles.page}>
      <BrandLock square={40} wordmark={typography.fontSize.xl} className={styles.brand} />
      {challenge.codeSent ? (
        <Card title={t.t('admin.panel.signIn.codeTitle')} className={styles.card}>
          <p className={styles.body}>
            {t.t('admin.panel.signIn.codeBody', { email: challenge.email ?? email })}
          </p>
          <form onSubmit={submitCode} className={styles.form}>
            <Field id="admin-otp-code" label={t.t('admin.panel.signIn.codeLabel')}>
              <Input
                id="admin-otp-code"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                // `maxLength` YOK (sıra 111, web B-22 ile aynı): "Kodunuz 123456"
                // yapıştırması süzgeçten önce kesilirdi. Kırpma süzgeçten sonra.
                value={code}
                onChange={(event) =>
                  setCode(extractOtpDigits(event.target.value, STAFF_LIMITS.loginCodeLength))
                }
                invalid={verifyState.status === 'failed'}
                required
              />
            </Field>
            {/*
              HATA NESNESİ HAM GEÇER. `ErrorText` çeviriyi KENDİ yapar; önceden
              çevrilmiş bir CÜMLE verilirse onu bir hata KODU sanar, sözlükte
              bulamaz ve "Beklenmeyen bir hata oluştu" der. Bu ekranda tam
              olarak bu oluyordu (panel şifresi turu, gerçek tarayıcıda
              ölçüldü); `error-message.guard.spec` sınıfı artık tarıyor.
            */}
            <ErrorText error={verifyState.error} />
            <div className={styles.actions}>
              {networkChanged ? (
                <Button type="button" variant="primary" onClick={changeEmail}>
                  {t.t('admin.panel.signIn.reenterPassword')}
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  // Sunucu şemasıyla aynı uzunluk (STAFF_LIMITS); süzgeç yalnız rakam bırakır.
                  disabled={
                    verifyState.status === 'loading' || code.length !== STAFF_LIMITS.loginCodeLength
                  }
                >
                  {t.t('admin.panel.signIn.verify')}
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={changeEmail}>
                {t.t('admin.panel.signIn.changeEmail')}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card title={t.t('admin.panel.signIn.title')} className={styles.card}>
          <p className={styles.body}>{t.t('admin.panel.signIn.body')}</p>
          <form onSubmit={submitCredentials} className={styles.form}>
            <Field id="admin-otp-email" label={t.t('admin.panel.signIn.emailLabel')}>
              <Input
                id="admin-otp-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t.t('admin.panel.signIn.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                invalid={requestState.status === 'failed'}
                required
              />
            </Field>
            <Field id="admin-password" label={t.t('admin.panel.signIn.passwordLabel')}>
              <Input
                id="admin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                invalid={requestState.status === 'failed'}
                required
              />
            </Field>
            {/*
              Sunucu hatası ALAN ALTINDA değil ortak bölgede yazılır ve bu
              BİLİNÇLİDİR: `STAFF_CREDENTIALS_INVALID` tek bir alanı değil
              e-posta+şifre İKİLİSİNİ reddeder. Hangi alanın yanlış olduğunu
              yazmak numaralandırma kapısını açardı.
            */}
            <ErrorText
              error={
                requestState.error ?? (auth.sessionExpiredNotice ? SESSION_EXPIRED_NOTICE : null)
              }
            />
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={requestState.status === 'loading'}>
                {t.t('admin.panel.signIn.continueWithPassword')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => void navigate(ROUTES.setup)}>
                {t.t('admin.panel.signIn.haveInvitation')}
              </Button>
            </div>
          </form>
        </Card>
      )}
      <p className={styles.note}>{t.t('admin.panel.signIn.restricted')}</p>
    </PageShell>
  );
}
